import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ReactComponent as SearchIcon } from 'assets/svg/search.svg';
import { debounce, get } from 'lodash';
import { cache } from 'graph_ql/index';
import { useQuery } from '@apollo/client';
import { AlgoliaInput, AlgoliaResponse } from 'graph_ql/types/types';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Spinner } from 'react-bootstrap';
import { useStyles } from './style';
import { GET_ALL_FEEDBACKS } from 'graph_ql/feedback/queries';
import { Feedback } from 'graph_ql/feedback/types';
import { FeedbackSuggestion } from './type';
import { getDefaultUserProfilePhoto } from 'utils/getUserProfilePhoto';
import { Box } from '@material-ui/core';
import { flattenFeedbackOptions } from 'utils/flattenFeedbackOptions';

const ConsumerFeedbacks = (props) => {
    const { docId } = props;
    const [searchTerm, setSearchTerm] = useState('');
    const [feedbackSuggestions, setFeedbackSuggestions] = useState<FeedbackSuggestion[]>([]);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [currentPage, setCurrentPage] = useState(0);
    const getMoreTimeout = useRef<any>(null);

    const getAllFeedbacksVariables = useMemo(() => {
        cache.modify({
            fields: {
                algoliaGetAllFeedbacks(existingTaskRefs, { readField }) {
                    return {};
                },
            },
        });
        return {
            query: searchTerm,
            filters: `docId: ${docId}`,
            page: 0,
            hit: 20,
        };
        // eslint-disable-next-line
    }, [searchTerm]);

    const { fetchMore } = useQuery<AlgoliaResponse<Feedback, 'algoliaGetAllFeedbacks'>, AlgoliaInput>(
        GET_ALL_FEEDBACKS,
        {
            variables: getAllFeedbacksVariables,
            fetchPolicy: 'network-only',
            onCompleted(response) {
                if (!response) return;
                onGetFeedbacks(response, false);
            },
        },
    );

    const onGetFeedbacks = (response, fetch) => {
        if (get(response, ['algoliaGetAllFeedbacks', 'hits'], []).length) {
            const data = response.algoliaGetAllFeedbacks.hits.map((consumerFeedback) => {
                return {
                    id: consumerFeedback?.id,
                    consumerFullName: `${consumerFeedback?.consumer?.firstName} ${consumerFeedback?.consumer?.lastName}`,
                    profileImageUrl: consumerFeedback?.consumer?.profileImageUrl,
                    gender: consumerFeedback?.consumer?.gender,
                    feedbackReasons: consumerFeedback?.feedbackReasons,
                    feedbackSubReason: consumerFeedback?.feedbackSubReason,
                    updated: consumerFeedback?.updated,
                };
            });
            if (response.algoliaGetAllFeedbacks.page >= response.algoliaGetAllFeedbacks.nbPages - 1) setHasMore(false);
            else {
                setHasMore(true);
                setCurrentPage(response?.algoliaGetAllFeedbacks?.page);
            }
            if (fetch) setFeedbackSuggestions((prevData) => [...(prevData || []), ...(data || [])]);
            else setFeedbackSuggestions(data);
        } else {
            setHasMore(false);
        }
    };

    useEffect(() => {
        setCurrentPage(0);
    }, []);

    const fetchMoreData = async (page) => {
        const data = await fetchMore({
            variables: {
                page: page,
                hit: 20,
            },
        });
        onGetFeedbacks(data?.data, true);
    };

    useEffect(() => {
        if (feedbackSuggestions?.length === 0 && hasMore) {
            setCurrentPage((prev) => prev + 1);
            if (getMoreTimeout.current) {
                clearTimeout(getMoreTimeout.current);
            }
            getMoreTimeout.current = setTimeout(() => {
                fetchMoreData(currentPage + 1);
            }, 1500);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasMore]);

    const classes = useStyles();

    const debounceSearch = useRef(debounce((value) => {}, 300)).current;

    const handleChangeSearchTerm = (event) => {
        const value = event.target.value;
        setSearchTerm(value);
        debounceSearch(value);
    };

    return (
        <div className={classes.sortWrap}>
            <div className={classes.searchBox}>
                <SearchIcon />
                <div style={{ width: '100%' }}>
                    <input
                        value={searchTerm}
                        placeholder="Search for Consumer"
                        onChange={handleChangeSearchTerm}
                        className={classes.input}
                    />
                </div>
            </div>
            <div id={'scrollableDiv'} className={classes.selectedOptions}>
                <InfiniteScroll
                    dataLength={feedbackSuggestions?.length}
                    next={() => {
                        setCurrentPage((prev) => prev + 1);
                        if (getMoreTimeout.current) {
                            clearTimeout(getMoreTimeout.current);
                        }
                        getMoreTimeout.current = setTimeout(() => {
                            fetchMoreData(currentPage + 1);
                        }, 1500);
                    }}
                    hasMore={hasMore}
                    loader={
                        <div className={classes.noSuggestions}>
                            <Spinner animation="border" style={{ width: 20, height: 20 }} />
                        </div>
                    }
                    scrollableTarget="scrollableDiv"
                >
                    {feedbackSuggestions?.map((feedback) => (
                        <div key={feedback.id} className={classes.feedbackContainer} style={{ height: 400 }}>
                            <div className={classes.feedbackUser}>
                                <div className={classes.feedbackUserContainer}>
                                    <img
                                        src={
                                            feedback?.profileImageUrl ||
                                            getDefaultUserProfilePhoto({ gender: feedback?.gender })
                                        }
                                        alt=""
                                        className={classes.suggestionAvatar}
                                    />
                                    <div className={classes.feedbackUserName}>{feedback?.consumerFullName}</div>
                                </div>
                                <p className={classes.feedbackCreated}>
                                    {Intl.DateTimeFormat('en-US', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                        hour: 'numeric',
                                        minute: 'numeric',
                                        second: 'numeric',
                                        hour12: true,
                                    }).format(new Date(feedback?.updated))}
                                </p>
                            </div>
                            <Box
                                width={'100%'}
                                height="1px"
                                bgcolor={'#D1D1D1'}
                                marginTop={'18px'}
                                marginBottom={'12px'}
                            ></Box>
                            <div className={classes.feedbackList}>
                                {flattenFeedbackOptions(feedback?.feedbackReasons).map((category) => (
                                    <div className={classes.category}>
                                        <p className={classes.categoryName}>{category.category}</p>
                                        <div className={classes.listOptions}>
                                            {category.options
                                                .sort(
                                                    (a, b) =>
                                                        a.displayOrderWithInCategory - b.displayOrderWithInCategory,
                                                )
                                                .map((option, index) => (
                                                    <>
                                                        <p className={classes.feedbackItem} key={option?.id}>
                                                            {option?.value === 'Other'
                                                                ? feedback?.feedbackSubReason
                                                                : option?.title}
                                                        </p>
                                                    </>
                                                ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </InfiniteScroll>
            </div>
        </div>
    );
};

export default ConsumerFeedbacks;
